import { lang } from '@umu/ukit-lang';
import { E_ACCESS_PERMISSION } from './common';
import { SwitchNum, SwitchString } from '../';
import { EMediaType, ResourceType } from '../../';

export interface BASE_PRACTICE {
    id?: string;
    obj_id: string;
    obj_type: PRACTICE_TYPE;
}

export interface ObjListItem extends BASE_PRACTICE {}

interface IVideoInfo {
    cover_img_url: string;
}

interface IUserInfo {
    teacher_id: string;
    student_id: string;
    user_name: string;
    login_name: string;
    email: string;
    phone: string;
    avatar: string;
}

interface IChatbotInfo {
    score: number;
    full_score: number;
    user_info: IUserInfo;
}

export interface IVideoCoverItem {
    obj_type: number;
    practice_time: number;
    homework_id?: string; // 可选字段
    practice_id: string | number;
    video_info: IVideoInfo;
}

export interface IChatbotCoverItem {
    practice_time: number;
    obj_type: number;
    practice_id: number;
    chatbot_info: IChatbotInfo;
}

export interface IPracticeChannel {
    id: string;
    name: string;
    show_index: string;
    color: string;
    access_permission: E_ACCESS_PERMISSION;
    is_publish: SwitchNum;
    obj_list: ObjListItem[];
    practice_num: number;
    practice_infos?: Array<IVideoCoverItem | IChatbotCoverItem>;
}

export interface IPracticeChannle extends IPracticeChannel {};


export enum PRACTICE_TYPE {
    HOMEWORK_VIDEO = 1,
    EXAM = 2,
    CHATBOT = 3
}

export enum PRACTICE_TYPE_NAME {
    HOMEWORK_VIDEO = 'video',
    EXAM = 'exam',
    CHATBOT = 'chatbot'
}

export const mapPracticeName2Type: {[props: string]: number} = {
    [PRACTICE_TYPE_NAME.HOMEWORK_VIDEO]: PRACTICE_TYPE.HOMEWORK_VIDEO,
    [PRACTICE_TYPE_NAME.EXAM]: PRACTICE_TYPE.EXAM,
    [PRACTICE_TYPE_NAME.CHATBOT]: PRACTICE_TYPE.CHATBOT
};

export const mapPracticeType2Name: {[props: number]: string} = {
    [PRACTICE_TYPE.HOMEWORK_VIDEO]: PRACTICE_TYPE_NAME.HOMEWORK_VIDEO,
    [PRACTICE_TYPE.EXAM]: PRACTICE_TYPE_NAME.EXAM,
    [PRACTICE_TYPE.CHATBOT]: PRACTICE_TYPE_NAME.CHATBOT
};

export interface IPracticeObj {
    id: string;
    obj_id: string;
    obj_type: PRACTICE_TYPE;
    title: string;
    is_open: SwitchString;
    show_index?: number;
    share_url?: string;
}

export enum PRACTICE_RECYCLE_TYPE {
    CHANNEL = 'channel',
    PRACTICE = 'practice'
}

interface ResourceMicroPage {
    title: string;
    share_url: string;
}

export interface ResourceCourse {
    title: string;
    head_img: string;
    chapter_num: number;
    session_num: string;
    share_url: string;
}

interface ResourceProgram {
    title: string;
    head_img: string;
    module_num: number;
    group_num: number;
    share_url: string;
}

export interface ResourceSession {
    title: string;
    cover_img: string;
    course_title: string;
    share_url: string;
}

export interface ResourceVideo {
    id: number;
    id_prefix_value: number;
    id_prefix_type: string;
    resource_id: number;
    file_name: string;
    file_duration: string;
    url: string;
    transcoding_url: string;
    transcoding_ext: string;
    thumb_info: string[];
    vod_file_id: string;
    vod_transcoding_url: string;
    status: TStatus;
    is_recycle: number;
    is_deleted: number;
    regular_deleted_by_eid: number;
    is_live: number;
    live_session_status: string;
    file_size: number;
    create_time: number;
    resource_type: number;
}

export interface ResourceDoc {
    resource_id: number;
    id_prefix_value: string;
    id_prefix_type: string;
    file_name: string;
    file_size: number;
    url: string;
    pdf_url: string;
    ext: string;
    document_id: string;
    transcoding_ext: string;
    thumb_info: string;
    status: TStatus;
    create_time: number;
    delete_time: number;
    is_recycle: number;
    is_deleted: number;
    extend: {
        img_arr: {
            path: string;
            list: string[];
        };
    };
    resource_type: number;
}

export type TPracticeResourceInfo =
    | ResourceMicroPage
    | ResourceCourse
    | ResourceProgram
    | ResourceSession
    | ResourceVideo
    | ResourceDoc;

export enum EPRACTICE_RESOURCE_TYPE {
    MICRO_PAGE = 1,
    COURSE = 2,
    PROGRAM = 3,
    SESSION = 4,
    VIDEO = 5,
    DOC = 6
}

export interface IPracticeResource {
    resource_id: 'string';
    resource_type: EPRACTICE_RESOURCE_TYPE;
    resource_info: TPracticeResourceInfo;
}

export const statusMap = {
    1: `${lang('上传中')}...`,
    2: `${lang('上传中')}...`,
    3: `${lang('上传失败')}`,
    4: `${lang('上传失败')}`,
    5: `${lang('上传失败')}`,
    6: `${lang('转码中')}...`,
    wait_transcoding: `${lang('转码中')}...`,
    transcoding: `${lang('转码中')}...`,
    transcoding_fail: lang('转码失败'),
    in_use: lang('可用'),
    wait_audit: lang('待审核'),
    audit_fail: lang('审核失败')
};

export type TStatus = keyof typeof statusMap;

export interface IFileResource {
    ext: string;
    url: string;
    psign?: string;
    app_id?: string;
    vod_file_id?: string;
    file_name: string;
    file_size: number;
    file_duration?: Second;
    file_type: string;
    percent: string;
    resource_id?: string;
    resource_type: 1 | 2;
    status: TStatus;
    thumbImg: string;
    uniqueId: string;
    media_type: EMediaType;
}

export interface IPreviewResource {
    resource_id: string;
    name: string;
    obj_type: EPRACTICE_RESOURCE_TYPE;
    resource_type?: ResourceType;
    status?: TStatus;
    resource_info?: TPracticeResourceInfo;
}

export interface Account {
    avatar?: string;
    account: string;
    account_type: string; // 'user' | 'department' | 'group';
    umu_id?: string;
    id: string;
}

export interface IFilterOption {
    submit_start_ts?: number;
    submit_end_ts?: number;
    accounts?: Account[];
}

export enum ESwitchStatus {
    OFF = 0,
    ON = 1
}

export enum EPracticeShieldStatus {
    UN_SHIELD = 0,
    SHIELD = 1
}

export enum E_PERMISSION_DENY_TYPE {
    NOT_EXIST = 1,
    CLOSED,
    NO_PERMISSION
}

export type Nullable<T> = T | null;

export interface IPracticeDetailRequestParams extends ObjListItem {
    channel_id: string;
}

export interface IPracticeDetail {
    id: string;
    title: string;
    is_open: string;
    obj_id: string;
    show_index: number;
    obj_type: string;
    share_url: string;
}

export interface IChannelStat {
    person_number: number; // 参与人数
    practice_number: number; // 练习次数
    practice_time: number; // 练习总时长
}

export type IPracticeDetailResponse = IResponse<IPracticeDetail>;
